import Spinner from 'common/spinner/components/spinner';
import { DynamicActions } from 'common/toolbar';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'ui';
import { withRouter } from 'utils/withRouter';
import {
  getWorklistMetaDataRequest,
  refreshWorkgrid,
} from '../../worklist/containers/worklist-actions';
import FilterBarContainer from '../../worklist/containers/worklist-toolbar-container';
import WorkGrid from './workgrid';

const WorkGridWrapper = ({
  menuVisible,
  filterWidth,
  isPlanningBoard = false,
  fullScreen,
  split,
  columnActions,
  addedBlockIds,
  setSelectedOrderIds,
  intl,
  clearWorklistData,
  selectable,
  hasIconsColumn = false,
  rowSelection = 'multiple',
  rowHeight = 30,
  rowDragMultiRow = false,
  sizeToFit = false,
  draggable = false,
  masterDetail = false,
  worklistId,
  isTickets = false,
  isMainTicketPage = false,
  fixedFilterValues = {},
}) => {
  const [selectedIdsState, setSelectedIdsState] = useState([]);
  const [availableActionsState, setAvailableActionsState] = useState({});
  const [isPlanningSelected, setIsPlanningSelected] = useState(false);

  const totalDataCount = useSelector(state => state.worklist.tableData.totalDataCount);
  const { activeView, metaData = {} } = useSelector(state => state.worklist);
  const data = useSelector(state => state.worklist.tableData.data);
  const { isLoadingMetaData, isLoadingViews } = useSelector(state => state.worklist);
  const dispatch = useDispatch();

  useEffect(() => {
    if (worklistId) {
      dispatch(getWorklistMetaDataRequest({ worklistId }));
    }
    return () => clearWorklistData();
  }, [worklistId]);

  const renderDynamicActions = () => <DynamicActions
    reload={() => {
      setSelectedIdsState([]);
      setAvailableActionsState({});
    }}
    showAsQuickAccess={true}
    availableActions={availableActionsState}
    isPlanningBoard={isPlanningBoard}
    isPlanningSelected={isPlanningSelected}
    worklistId={worklistId}
  />;

  const checkLimiters = (action, selectedRows) => {
    if (action.limiter) {
      const limiters = action.limiter.split(',');

      return limiters.every((limiter) => {
        const actionForFirstRow =
          selectedRows &&
          selectedRows.length &&
          selectedRows[0].actions?.find(r => r.id === action.id);
        const expectedValueForFirstRow = actionForFirstRow && actionForFirstRow.payload[limiter];

        return (
          selectedRows &&
          selectedRows.length &&
          selectedRows.every((row) => {
            const actionForCurrentRow = row.actions && row.actions?.find(r => r.id === action.id);
            const actualValue = actionForCurrentRow && actionForCurrentRow.payload[limiter];
            return actualValue === expectedValueForFirstRow;
          })
        );
      });
    }
    return true;
  };

  const handleRowSelect = (selectedRows) => {
    const allActions = selectedRows.reduce((actions, row) =>
      [...actions, ...row?.actions || []],
      []);
    setIsPlanningSelected(selectedRows?.find(r => r.modelType === 'planning'));
    const ids = selectedRows?.map(r => r.id);
    if (setSelectedOrderIds) {
      setSelectedOrderIds(isPlanningSelected ? selectedRows : ids);
    }

    // If the selectedRows consist of orders & notifications/calls,
    // show warning notification.
    if (
      selectedRows.some(r => r.type === 'order') &&
      selectedRows.some(r => r.type === 'call' || r.type === 'notification')
    ) {
      notification.warning({
        message: intl.formatMessage({
          id: 'timeline.toolbar.MSG_PROCESSED_TOGETHER',
        }),
        placement: 'top',
      });
      setSelectedIdsState(ids);
      setAvailableActionsState({});
      return;
    }

    // Create a object with all availble actions.
    // key: actionId, value: array of payload to pass
    const availableActions = allActions.reduce((actions, action) => {
      // All rows selected should have the action available, or the button will dissapear
      if (selectedRows.every(row => row.actions?.find(r => r.id === action.id))) {
        // also, check if there is a limiter & if the limiter applies to all rows.
        if (checkLimiters(action, selectedRows)) {
          return {
            ...actions,
            [action.id]: actions[action.id]
              ? [...actions[action.id], action.payload]
              : [action.payload],
          };
        }
      }
      return actions;
    }, {});
    setSelectedIdsState(ids);
    setAvailableActionsState(availableActions);
  };

  useEffect(() => {
    if (addedBlockIds) {
      setSelectedIdsState(state => state?.filter(s => !addedBlockIds?.find(i => i === s)));
      handleRowSelect(selectedIdsState?.filter(s => !addedBlockIds?.find(i => i === s)));
    }
  }, [addedBlockIds]);

  const renderContent = () => {
    if (isLoadingMetaData || isLoadingViews) {
      return <Spinner />;
    }
    if (!activeView?.id || !Object.keys(metaData)?.length) {
      return null;
    }
    return <WorkGrid
      columnActions={columnActions}
      handleRowSelect={handleRowSelect}
      columnDef={metaData.columnDefinitions}
      bufferSize={data?.length}
      selectable={selectable}
      hasIconsColumn={hasIconsColumn}
      rowSelection={rowSelection}
      rowHeight={rowHeight}
      rowDragMultiRow={rowDragMultiRow}
      sizeToFit={sizeToFit}
      draggable={draggable}
      masterDetail={masterDetail}
      isPlanningBoard={isPlanningBoard}
      isTickets={isTickets}
      worklistId={worklistId}
      isMainTicketPage={isMainTicketPage}
      fixedFilterValues={fixedFilterValues}
      selectedIds={selectedIdsState}
    />;
  };

  return (
    <>
      <FilterBarContainer
        menuVisible={menuVisible}
        columnDef={metaData.columnDefinitions}
        isLoading={false}
        renderDynamicActions={renderDynamicActions}
        filterWidth={filterWidth}
        data={data}
        isPlanningBoard={isPlanningBoard}
        isTickets={isTickets}
        split={split}
        fullScreen={fullScreen}
        isNewWorklist={true}
        totalDataCount={totalDataCount}
        fixedFilterValues={fixedFilterValues}
        refresh={() => dispatch(refreshWorkgrid(true))}
      />
      {renderContent()}
    </>
  );
};
WorkGridWrapper.propTypes = {
  menuVisible: PropTypes.bool,
  filterWidth: PropTypes.number,
  isPlanningBoard: PropTypes.bool,
  fullScreen: PropTypes.bool,
  split: PropTypes.bool,
  columnActions: PropTypes.object,
  addedBlockIds: PropTypes.array,
  setSelectedOrderIds: PropTypes.func,
  intl: PropTypes.object,
  clearWorklistData: PropTypes.func,
  selectable: PropTypes.bool,
  hasIconsColumn: PropTypes.bool,
  rowSelection: PropTypes.string,
  rowHeight: PropTypes.number,
  rowDragMultiRow: PropTypes.bool,
  sizeToFit: PropTypes.bool,
  draggable: PropTypes.bool,
  masterDetail: PropTypes.bool,
  worklistId: PropTypes.string,
  isTickets: PropTypes.bool,
  isMainTicketPage: PropTypes.bool,
  fixedFilterValues: PropTypes.object,
};

export default withRouter(injectIntl(WorkGridWrapper));
